// ── Kalshi Account Types ─────────────────────────────────────────
// All types for the account plane (credentials, positions, orders, fills).
// Completely separate from Hydra/market types.

// ── Credentials & Keys ──────────────────────────────────────────

export interface KalshiKey {
  id: string;              // internal UUID
  accessKey: string;       // Kalshi access key ID
  privateKeyPem: string;   // PKCS#8 PEM string
  label: string;           // user-visible name
  addedAt: number;         // Date.now() when added
}

export interface KalshiCredentials {
  environment: 'prod' | 'demo';
  keys: KalshiKey[];
}

// ── Positions ──────────────────────────────────────────��────────

export interface KalshiPosition {
  ticker: string;
  side: 'yes' | 'no';
  position: number;        // always positive (unsigned contract count)
  averagePrice: number;    // cents 0-100
  /** Canonical most-recent fill for this ticker/side aggregate. Org-key
   * relay positions provide it; live venue aggregates may not. */
  lastFillAt?: string | null;
  // Exact entry cost in cents from the relay's fills ledger (V2 org-key
  // path only). Preferred over averagePrice × position, which re-multiplies
  // a display-rounded avg and drifts on fractional/many-fill positions.
  costBasisCents?: number;
  realizedPnl: number;     // cents
  // Total fees Kalshi has charged for this (ticker, side) so far, in cents.
  // Populated from /portfolio/positions.fees_paid_dollars on the personal-key
  // path; undefined for org-key positions until per-fill fee tracking lands
  // (the relay's fills ledger doesn't carry fees yet).
  feesPaidCents?: number;
  /** Opening fees on the OPEN share only — the unrealized-recipe fee input. */
  openFeesCents?: number;
  /** Lifetime bought / sold contracts for this (ticker, side) — the
   *  full-position anchor (#91). `position` is the post-sale remainder, so
   *  original qty and its cost/to-win must be derived from these. Org-key
   *  relay positions provide them; live venue aggregates may not. */
  totalBoughtCount?: number;
  totalSoldCount?: number;
  /** Lifetime buy cost in cents — the original entry basis, un-rounded.
   *  Fee-exclusive (see the relay's OpenPosition jsdoc). */
  totalBuyCostCents?: number;
}

// ── Fills ──────���────────────────────────────────���────────────────

export interface KalshiFill {
  fillId: string;          // dedup key
  orderId: string;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  price: number;           // cents 0-100
  createdAt: string;       // ISO timestamp
}

// ── Orders ────���──────────────────────────────────��──────────────

export interface KalshiOrder {
  orderId: string;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  type: 'limit' | 'market';
  status: string;
  count: number;
  remainingCount: number;
  /** Lifetime matched contracts for this order (Kalshi `fill_count`). Exact —
   *  unlike the page-session recent-fills window, which caps at 100 fills.
   *  Absent on payload shapes that don't send it. */
  fillCount?: number;
  /** Contracts removed by queue-preserving decreases (Kalshi `decrease_count`).
   *  Distinguishes a shrunken order from a filled one when `fill_count` is absent. */
  decreaseCount?: number;
  yesPrice?: number;       // cents
  noPrice?: number;        // cents
  createdTime: string;     // ISO timestamp
  /** Unix seconds. Absent / null means GTC (rests until cancel or contract
   * close). Present means the order auto-cancels at this time — typically
   * either game start (GTS in sports parlance) or a custom user expiration. */
  expirationTs?: number | null;
  /** Kalshi `client_order_id`, when the venue echoes it on reads. Dash orders
   *  don't set one; oscar sequence legs do (`osc-<uuid>-take` / `osc-<uuid>-blk`)
   *  — the `-blk` suffix is how the UI recognizes a resting blocker. */
  clientOrderId?: string;
  /** Relay-verified recovery evidence for an order omitted from Kalshi's
   * ordinary resting-order list. Exact detail proves the order still rests
   * and remains cancellable, but not that an amend/decrease is safe on the
   * old listing. */
  reconciliation?: {
    source: 'exact_order_detail';
    lifecycle: 'resting_list_omitted';
    verifiedAt: string;
    marketStatus: string | null;
    marketStatusCheckedAt: string | null;
    marketInactive: boolean | null;
    actions: {
      cancel: 'available';
      decrease: 'unavailable';
    };
  };
}

/** Shared UI/store authority for recovered list-omitted orders. */
export function canEditKalshiOrder(order: KalshiOrder): boolean {
  return order.reconciliation?.actions.decrease !== 'unavailable';
}

// ── Oscar overtake/blocker sequence (POST /api/kalshi/order-sequence) ─────

export type OscarLegRole = 'cancel' | 'kill' | 'take' | 'blocker';

export interface OscarSequenceLeg {
  role: OscarLegRole;
  ok: boolean;
  orderId?: string | null;
  side?: 'yes' | 'no';
  priceCents?: number;
  count?: number;
  /** Take-leg contracts filled immediately (null/absent = venue didn't say). */
  filledCount?: number | null;
  alreadyGone?: boolean;
  /** True when a retry reused the original client_order_id and the relay
   * recovered the already-accepted venue order instead of placing it twice. */
  alreadyPlaced?: boolean;
  skipped?: boolean;
  error?: string;
  code?: string;
  retryable?: boolean;
  /** Kill legs only (#472): true when the killed blocker was the firing
   * trader's own (self-overtake). */
  ownBlocker?: boolean;
  /** Kill legs only: the killed blocker's resting price. */
  blockerPriceCents?: number;
}

export interface OscarSequenceResult {
  ok: boolean;
  sequenceId: string;
  op: 'overtake' | 'release_overtake';
  ticker: string;
  takePriceCents: number;
  /** null when no blocker leg was requested. */
  blockerPriceCents: number | null;
  /** Count of failed kill legs (informational — a failed kill never fails the
   * sequence; the maker-STP take is the backstop). Absent when zero. */
  warnings?: number;
  legs: OscarSequenceLeg[];
}

export interface OscarSequenceInput {
  /** Stable identity for one logical fire. Reuse it only when retrying the
   * exact same sequence after an ambiguous/partial result. */
  sequenceId?: string;
  /** True only when resubmitting the exact same logical fire after an
   * ambiguous or partial result. Lets the relay recover accepted legs before
   * applying caps to the still-missing work. */
  retry?: boolean;
  op: 'overtake' | 'release_overtake';
  ticker: string;
  /**
   * Observed market context for diagnostics/retry identity only. The account
   * store deliberately strips it from the relay request until #575 owns write
   * routing. Zero is valid; absent remains unknown.
   */
  exchangeIndex?: number;
  /** Take-leg side; the relay computes the blocker on the opposite side. */
  side: 'yes' | 'no';
  take: { priceCents: number; count: number };
  /** Omit for a blocker-less overtake. REQUIRED for release_overtake. */
  blocker?: { count: number };
  /** Unix seconds; applied to both resting legs. */
  expirationTs?: number;
  /** Required iff op === 'release_overtake'. */
  cancelOrderId?: string;
}

// ── Oscar sequence RECEIPT (#302) ────────────────────────────────────────
//
// Read-only wire contract for GET /api/trader/oscar-receipt and
// GET /api/manager/traders/:id/oscar-receipt. This is a PROJECTION over the
// durable trades audit, not a second lifecycle authority: it never affects
// placement, and `OscarSequenceInput` / `OscarSequenceResult` above are
// untouched by it.
//
// `priceDollars` is the audit's exact decimal string ('0.6000'). Keep it a
// string end to end — formatting shifts the decimal point textually so a
// 60.5¢ order can never render as 60.49999999999999¢.

export type OscarReceiptLegState =
  | 'placed'
  /** An idempotent retry recovered an order the venue had already accepted. */
  | 'already_placed'
  /** The relay's trusted sequence plan proves this leg was not requested. */
  | 'not_requested'
  /** The relay's trusted sequence plan included this leg, but no row was written. */
  | 'skipped'
  /** No audit row exists. The write path records nothing for a leg that was
   *  never requested AND nothing for one that was skipped on legacy rows, so
   *  this label deliberately refuses to distinguish the two. */
  | 'skipped_or_not_requested'
  | 'rejected'
  /** An Oscar release cancelled the old blocker before the replacement legs. */
  | 'canceled_before_replace'
  /** The venue accepted a leg the relay could not confirm — operator action. */
  | 'recovery_pending'
  | 'unknown';

export type OscarReceiptState =
  | 'complete'
  | 'partial'
  | 'recovery_pending'
  | 'rejected'
  | 'unknown';

export interface OscarReceiptLeg {
  role: OscarLegRole;
  state: OscarReceiptLegState;
  side: 'yes' | 'no' | null;
  count: number | null;
  /** Exact fixed-point audit string, e.g. '0.6000'. Never a number. */
  priceDollars: string | null;
  venueOrderId: string | null;
  message: string | null;
}

export interface OscarSequenceReceipt {
  sequenceId: string;
  op: 'overtake' | 'release_overtake' | 'unknown';
  ticker: string;
  takeSide: 'yes' | 'no' | null;
  sequenceStartedAt: string;
  lastUpdatedAt: string;
  state: OscarReceiptState;
  legs: OscarReceiptLeg[];
  /** What the trader must verify. Non-null whenever the sequence is not a
   *  clean complete/rejected outcome. */
  guidance: string | null;
  /** #597/#640 — release_overtake protection lifecycle, relay-derived.
   *  Null for plain overtakes; ABSENT on receipts that predate the field
   *  (legacy relays) — render nothing rather than a guessed state. */
  protection?: OscarProtectionProvenance | null;
}

export type OscarProtectionState =
  | 'retained'
  | 'released'
  | 'replacement_pending'
  | 'replacement_failed'
  | 'restored'
  | 'unresolved';

export interface OscarProtectionProvenance {
  state: OscarProtectionState;
  releaseEvidence: 'sequence_cancel_accepted' | 'already_gone_unattributed' | null;
  releasedOrderId: string | null;
  guidance: string | null;
}

export interface OscarReceiptResponse {
  receipt: OscarSequenceReceipt | null;
}

/** #314 — durable one-click receipts. Mirrors relay/src/services/oneClickReceipt.ts. */
export type OneClickReceiptState =
  | 'rejected'
  | 'resting'
  | 'partially_filled'
  | 'filled'
  | 'cancelled'
  | 'unknown';

export interface OneClickReceipt {
  /** The `lt-<uuid>` client order id. The correlation key. */
  clientOrderId: string;
  ticker: string | null;
  side: 'yes' | 'no' | null;
  count: number | null;
  /** Exact audit decimal string, e.g. '0.6000'. Never a number. */
  priceDollars: string | null;
  createdAt: string;
  state: OneClickReceiptState;
  /** Independent of `state`: a partly-filled order can also be cancelled. */
  cancelled: boolean;
  /** Fractional, never rounded. */
  filledCount: number;
  venueOrderId: string | null;
  message: string | null;
  /** From the stored `__lt2` audit stamp: 'mouse' | 'hotkey-a' | 'hotkey-d'.
   *  Absent on pre-LT2 rows. */
  inputMethod?: string | null;
}

export interface OneClickReceiptsResponse {
  receipts: OneClickReceipt[];
}

export interface OrderInput {
  ticker: string;
  /**
   * Observed market context only. postOrderRaw deliberately does not serialize
   * it until the separate write-routing ticket owns that venue contract.
   */
  exchangeIndex?: number;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  type: 'limit' | 'market';
  count: number;
  price?: number;          // cents 0-100
  postOnly?: boolean;
  timeInForce?: 'fill_or_kill' | 'good_till_canceled' | 'immediate_or_cancel';
  expirationTime?: string;  // ISO 8601 timestamp for order expiry
  /** Reduce-only: shrink but never flip a position. Set on close/ripcord so a
   *  stale count can't over-fill past flat into an opposite position. */
  reduceOnly?: boolean;
  /** Caller-supplied idempotency/audit tag (Kalshi client_order_id). The
   *  one-click path stamps `lt-<uuid>` so audit rows join back to the input;
   *  ticket orders leave it unset (venue generates one). */
  clientOrderId?: string;
  /** Audit-only metadata stamped into the POST body as `__lt2`. The relay
   *  stores the body verbatim in trades.request_payload; legacyToV2Order
   *  allowlists the venue body, so this NEVER reaches Kalshi. */
  auditMeta?: Record<string, unknown>;
  /** Live Trading V3 Attack marker stamped into the POST body as `__attack`.
   *  Gate input AND audit stamp: the relay refuses the order unless the
   *  attack grant, ceiling, and accepted depth all hold; the venue-body
   *  allowlist keeps it off the wire. */
  attackMeta?: Record<string, unknown>;
}

// ── Key Health (for Settings UI) ────────────────────────────────

export interface BudgetUsage {
  used: number;
  limit: number;
}

export interface KeyHealth {
  keyId: string;
  label: string;
  accessKey: string;       // truncated for display (first 6...last 4)
  wsConnected: boolean;
  pollerActive: boolean;
  quarantinedUntil: number | null;
  budgetUsage: {
    trading: BudgetUsage;
    polling: BudgetUsage;
    general: BudgetUsage;
  };
  lastError: string | null;
}

// ── Account Connection Status ──────────���────────────────────────

export type AccountStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
